import { MenuItem, Position } from "@blueprintjs/core"
import { Suggest } from "@blueprintjs/select"
import { useLocalStorage, useDebouncedState } from "@mantine/hooks"
import { Prohibit } from "@phosphor-icons/react"
import CustomButton from "components/Atoms/CustomButton"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomTag from "components/Atoms/CustomTag"
import { AuthenticationContext } from "components/Molecules/Authentication"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import { PLOT_COMMODITY_STATUS } from "data/static.items"
import { useSearchPlotCommoditiesQuery } from "generated/graphql"
import { get } from "lodash-es"
import React, { useCallback, useContext, useEffect, useState } from "react"
import classNames from "utils/classNames"

export default function PlotCommoditySearch({
  fill,
  className,
  onSelect,
  handleRef,
  query,
  onQueryChange,
  selectedItem,
}) {
  const { user } = useContext(AuthenticationContext)
  const key = `defaultTraderVarieties-${user.currentDepartment?.id || "na"}`
  const changeKey = key + "-isChanged"
  const [varieties, setVarieties] = useLocalStorage({
    key,
    defaultValue: [],
  })
  const defaultTraderVarieties = user.currentDepartment?.defaultTraderVarieties
  useEffect(() => {
    if (defaultTraderVarieties?.length) {
      if (localStorage.getItem(changeKey) !== "1") {
        setVarieties(defaultTraderVarieties)
      }
    }
  }, [defaultTraderVarieties])

  const [isOpen, setOpen] = useState(false)
  const { data, loading, error } = useSearchPlotCommoditiesQuery({
    variables: {
      first: 15,
      query,
      companies: [],
      filter: {
        isArchived: false,
        varieties: varieties?.length ? varieties?.map(v => v.id) : [],
        status:
          user?.currentDepartment?.kiteReferenceId ===
          "dfa6a605-7a41-48d2-ad9c-306655935a8e"
            ? [
                PLOT_COMMODITY_STATUS.READY_FOR_HARVEST,
                PLOT_COMMODITY_STATUS.SCHEDULED_FOR_HARVEST,
              ]
            : [
                PLOT_COMMODITY_STATUS.HARVESTING_DONE,
                PLOT_COMMODITY_STATUS.HARVEST_IN_PROGRESS,
                PLOT_COMMODITY_STATUS.NOT_READY_FOR_HARVEST,
                PLOT_COMMODITY_STATUS.READY_FOR_HARVEST,
                PLOT_COMMODITY_STATUS.SCHEDULED_FOR_HARVEST,
              ],
      },
    },
  })

  const [debouncedQuery, setDebouncedQuery] = useDebouncedState(query, 800)

  useEffect(() => {
    onQueryChange(debouncedQuery)
  }, [debouncedQuery])

  const handleReset = useCallback(() => {
    onQueryChange("")
    onSelect({})
  }, [onQueryChange, onSelect])

  const plotCommodities = get(data, "searchPlotCommodity", [])
  useEffect(() => {
    if (plotCommodities.length > 0) {
      setOpen(true)
    } else {
      setOpen(false)
    }

    return function cleanup() {
      setOpen(false)
    }
  }, [plotCommodities])
  if (error) return error.message

  function handleSelect(item) {
    onSelect && onSelect(item)
  }

  function getTag(category) {
    if (category === "EXPORT") {
      return (
        <CustomTag
          intent="success"
          className="bg-green-100 text-green rounded-xl shadow-sm"
        >
          Export
        </CustomTag>
      )
    }
    if (category === "DOMESTIC") {
      return (
        <CustomTag
          intent="success"
          className="bg-yellow-100 text-yellow-500 rounded-xl shadow-sm"
        >
          Domestic
        </CustomTag>
      )
    }
  }

  function renderItem(item, { handleClick, modifiers }) {
    return (
      <MenuItem
        disabled={item.plot.farmer.isBlocked}
        className={`${item.plot.farmer.isBlocked ? "bg-red-100" : ""}`}
        key={`${item.id}-${item.name}`}
        active={modifiers.active}
        onClick={handleClick}
        text={
          <span className="flex items-center gap-2">
            {get(item, "name")}{" "}
            {get(item, "plot.farmer.isBlocked") ? (
              <Prohibit size={18} color="red" weight="fill" />
            ) : null}
          </span>
        }
        label={
          (
            <span>
              {getTag(get(item, "lastTradeProfile.category"))}{" "}
              {get(item, "variety.commodity.name")} ({" "}
              {get(item, "variety.name")} )
            </span>
          ) as any
        }
      />
    )
  }

  return (
    <div>
      <CustomFormGroup
        className={className}
        label="Commodity Supplier"
        required
      >
        <Suggest
          inputProps={{
            id: "vt_plot_commodity_search",
            inputRef: handleRef,
            autoFocus: true,
            fill,
            leftIcon: "search",
            rightElement: (
              <CustomButton
                loading={loading}
                icon="cross"
                onClick={handleReset}
                minimal
              />
            ),
          }}
          selectedItem={selectedItem}
          popoverProps={{
            isOpen,
            onClose: e => setOpen(false),
            popoverClassName: classNames("absolute", {
              "vt-plot-commodity-search-popover": plotCommodities.length > 0,
            }),
            targetClassName: "w-full",
            minimal: true,
            position: Position.BOTTOM_LEFT,
          }}
          inputValueRenderer={item => item.name}
          itemRenderer={renderItem}
          query={query}
          onQueryChange={setDebouncedQuery}
          items={plotCommodities}
          onItemSelect={handleSelect}
        />
      </CustomFormGroup>
      <OverFlowMenu
        items={defaultTraderVarieties}
        getIntent={item =>
          varieties?.find(v => v.id === item.id) ? "success" : "primary"
        }
        onClick={item => {
          const exists = varieties?.find(v => v.id === item.id)
          if (exists) {
            setVarieties(prev => prev.filter(v => v.id !== item.id))
          } else {
            setVarieties(prev => [item].concat(prev))
          }
          localStorage.setItem(changeKey, "1")
        }}
      />
    </div>
  )
}

PlotCommoditySearch.defaultProps = {
  fill: true,
}
